import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useAuth } from "../../AuthContext";

/**
 * PermissionRoute - A Route wrapper that checks claim-based permissions.
 * 
 * Props:
 * - requiredPermissions: array of permission claims (at least one must match)
 * - requireAll: if true, ALL permissions must match (default: false, meaning ANY match)
 * - component: the component to render
 * - All other Route props are passed through
 * 
 * Usage:
 *   <PermissionRoute 
 *     path="/PurchaseIndex" 
 *     requiredPermissions={["Read Transaction"]} 
 *     component={PurchaseIndex} 
 *   />
 */
const PermissionRoute = ({ component: Component, requiredPermissions = [], requireAll = false, ...rest }) => {
  const { permissions, isSuperAdmin, roleName, isAuthenticated } = useAuth();

  const checkPermission = () => {
    // SuperAdmin has access to everything
    if (isSuperAdmin || roleName === "SuperAdmin") return true;

    // If no permissions required, allow access
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    // Check permissions
    if (requireAll) {
      return requiredPermissions.every((perm) => permissions.includes(perm));
    }
    return requiredPermissions.some((perm) => permissions.includes(perm));
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated) {
          return <Redirect to="/" />;
        }
        if (checkPermission()) {
          return <Component {...props} />;
        }
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: '20px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '16px',
            }}>
              🚫
            </div>
            <h2 style={{
              color: '#4b0446',
              marginBottom: '8px',
              fontSize: '24px',
            }}>
              Access Denied
            </h2>
            <p style={{
              color: '#666',
              fontSize: '16px',
              maxWidth: '400px',
            }}>
              You do not have the required permissions to access this page. 
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        );
      }}
    />
  );
};

export default PermissionRoute;
